import { LanguageSelector } from '@/components/LanguageSelector.tsx'
import { trackAuthenticatedOnce } from '@/trackAuthenticatedOnce'
import { logger } from '@jobilla/instrumentation'
import { useCandidacyIndex } from '@jobilla/nexus-sdk/lib/hooks/useCandidacy'
import type { CandidacyListItemData } from '@jobilla/nexus-sdk/lib/types/CandidacyListItemData'
import { createFileRoute, Link, Navigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Centered } from '../components/Centered'
import { DisplayDate } from '../components/DisplayDate'

export const Route = createFileRoute('/organisations/$organisationId/')({
	head: () => ({
		meta: [{ title: 'Jobilla' }],
	}),
	component: SubmissionsScreen,
})

function SubmissionsScreen() {
	const { organisationId } = Route.useParams()
	const { t } = useTranslation('common')

	const candidaciesQuery = useCandidacyIndex(organisationId, { retry: 0 })

	useEffect(() => {
		if (!candidaciesQuery.isSuccess) {
			return
		}
		trackAuthenticatedOnce()
		logger.track('Candidate Center: Viewed candidacies', {
			data: { candidacy_count: candidaciesQuery.data.candidacies.length },
		})
		// Fire once per successful load — not again when the count changes on refetch.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [candidaciesQuery.isSuccess])

	if (candidaciesQuery.isPending) {
		return (
			<Centered>
				<p className="text-sm text-grey-500">{t('login.loading')}</p>
			</Centered>
		)
	}

	if (candidaciesQuery.isError) {
		const status = candidaciesQuery.error.response?.status

		if (status === 401) {
			return <Navigate to="/" replace={true} />
		}

		// An organisation the signed-in address never applied to is a 404 — send them back to the list.
		if (status === 404) {
			return <Navigate to="/organisations" replace={true} />
		}

		return (
			<Centered>
				<p className="text-lg font-medium text-grey-800">{t('login.error.title')}</p>
				<p className="max-w-sm text-sm text-grey-500">{t('submissions.error')}</p>
			</Centered>
		)
	}

	const { organisation, candidacies } = candidaciesQuery.data

	return (
		<div className="mx-auto flex min-h-screen w-full max-w-md flex-col px-6 py-12">
			<header className="flex items-center justify-between gap-4">
				<Link
					to="/organisations"
					className="text-sm font-medium text-grey-700 hover:text-grey-900"
				>
					‹ {t('submissions.back_to_organisations')}
				</Link>
				<LanguageSelector />
			</header>

			<h1 className="mt-8 text-lg font-semibold text-grey-900">{organisation.name}</h1>
			<p className="mt-1 text-sm text-grey-500">{t('submissions.title')}</p>

			{candidacies.length === 0
				? (
					<div className="mt-3 rounded-lg bg-grey-50 px-6 py-4">
						<p className="text-sm font-medium text-grey-800">{t('submissions.empty.title')}</p>
						<p className="mt-1 text-sm text-grey-500">
							{t('submissions.empty.body', { organisation: organisation.name })}
						</p>
					</div>
				)
				: (
					<ul className="mt-3 flex flex-col gap-2">
						{candidacies.map(candidacy => (
							<li key={candidacy.id}>
								<Link
									to="/organisations/$organisationId/$candidacyId"
									params={{ organisationId, candidacyId: candidacy.id }}
									onClick={() =>
										logger.track('Candidate Center: Opened submission', {
											data: { candidate_id: candidacy.id },
										})}
									className="flex items-center gap-4 rounded-lg bg-grey-50 px-6 py-4 hover:bg-grey-100"
								>
									{candidacy.logoUrl === null
										? null
										: (
											<img
												src={candidacy.logoUrl}
												alt=""
												className="h-10 w-10 shrink-0 rounded object-contain"
											/>
										)}
									<span className="min-w-0 flex-1">
										<span className="block truncate text-lg font-medium text-grey-900">
											{candidacy.campaignTitle}
										</span>
										<span className="mt-0.5 block text-sm text-grey-700">
											<LatestActivity candidacy={candidacy} />
										</span>
									</span>
									<span aria-hidden={true} className="text-xl text-grey-400">›</span>
								</Link>
							</li>
						))}
					</ul>
				)}
		</div>
	)
}

function LatestActivity({ candidacy }: { candidacy: CandidacyListItemData }) {
	const { t } = useTranslation('common')
	const submittedAt = new Date(candidacy.submittedAt)
	const cvUploadedAt = candidacy.cvUploadedAt === null ? null : new Date(candidacy.cvUploadedAt)

	if (cvUploadedAt !== null && cvUploadedAt > submittedAt) {
		return (
			<span className="flex gap-6">
				<span>{t('submissions.cv_uploaded')}</span>
				<DisplayDate date={cvUploadedAt} />
			</span>
		)
	}

	return <DisplayDate date={submittedAt} />
}
